import { WalletTypes } from '@constants/enums';
import CustomDrawer from '@efex-components/common/Drawer';
import { IEnterprisePayment } from '@efex-services/payments/types';
import {
  Alert,
  Button,
  StatusComponent,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@efexteam/components';
import {
  ColorsNeutral400,
  ColorsPrimary500,
  ColorsPrimary900,
} from '@efexteam/design-tokens';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Divider, IconButton, Typography } from '@mui/material';
import { formatCurrency, formatCurrencyToNumber } from '@utils/formater';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectWalletsData } from '@/modules/wallets/selectors';
import useForwardService from '../hooks/useForward.service';

interface PayForwardProps {
  onClose: () => void;
  refetchPayments: () => void;
  payment: IEnterprisePayment;
}

const PayForward = ({ onClose, refetchPayments, payment }: PayForwardProps) => {
  const { t, i18n } = useTranslation();
  const walletsState = useSelector(selectWalletsData);

  const {
    showConfirmDialog,
    forwardPayedLoading,
    setShowConfirmDialog,
    handleConfirm,
    handleReject,
  } = useForwardService({
    payment,
    onClose,
    refetchPayments,
  });

  const { language } = i18n;

  const { forwardRemainingInfo, contact, dueDate, executionDate, rate } =
    payment;

  if (!forwardRemainingInfo) {
    return null;
  }

  const {
    currency,
    desCurrency,
    remainingAmount,
    remainingDesAmount,
    remainingPercentage,
  } = forwardRemainingInfo;

  const walletFromPayment = walletsState?.find(
    (wallet) =>
      wallet.currency.name === currency &&
      wallet.wallet.wallet_type === WalletTypes.GLOBAL_ACCOUNT
  );

  const canUserPay =
    walletFromPayment &&
    Number(walletFromPayment.balance) >=
      formatCurrencyToNumber(remainingAmount.toString());

  const getRemainingDays = () => {
    const fixedDueDate = new Date(dueDate);
    const difference = fixedDueDate.getTime() - new Date().getTime();
    const days = Math.ceil(difference / (1000 * 3600 * 24));
    return days;
  };

  return (
    <>
      <CustomDrawer
        PaperProps={{ id: 'paymentWrapper' }}
        position="right"
        isOpen
        onClose={onClose}
      >
        <Box className="p-5 min-w-[450px] max-w-[500px]">
          <Box component="section" className="flex items-start justify-between">
            <Box>
              <StatusComponent
                displayVariant="chip"
                status="special"
                label={t(
                  forwardRemainingInfo.remainingPercentage > 0
                    ? 'stateForwardPending'
                    : 'stateWaitingForDueDate'
                )}
                size="small"
              />
              <Typography
                className="!my-3"
                variant="body2"
                style={{ color: 'var(--colors-extra-900)' }}
              >
                {t('pyfwdRemainingDays', { days: getRemainingDays() })}
              </Typography>
            </Box>
            <IconButton onClick={onClose}>
              <CloseOutlinedIcon className="text-[#BFBFBF]" />
            </IconButton>
          </Box>
          <Box component="section" className="my-4">
            <Typography color={ColorsNeutral400} variant="body2">
              {t('pyfwdAmountToSend')}
            </Typography>
            <Typography variant="h5">
              {formatCurrency(remainingAmount)} {currency}
            </Typography>
            <Typography
              color={ColorsNeutral400}
              sx={{ marginTop: '1rem' }}
              variant="body2"
            >
              {t('pyfwdAmountToReceive')}
            </Typography>
            <Typography variant="h5">
              {formatCurrency(remainingDesAmount)} {desCurrency}
            </Typography>
          </Box>
          <Box component="section">
            {remainingPercentage > 0 ? (
              <Alert
                variant="outlined"
                severity={canUserPay ? 'info' : 'error'}
              >
                {t(canUserPay ? 'pyfwdEnoughBalance' : 'pyfwdNotEnoughBalance')}
              </Alert>
            ) : (
              <Alert variant="outlined" severity="success">
                {t('pyfwdWaitingExecutionDate')}
              </Alert>
            )}
            <Box className="flex justify-center">
              <Button
                appearance="primary"
                className="!mt-5"
                disabled={!canUserPay || remainingPercentage <= 0}
                onClick={() => setShowConfirmDialog(true)}
              >
                {t('pyfwdConfirm')}
              </Button>
            </Box>
          </Box>
          <Divider className="!my-5" />
          <Box component="section">
            <Box className="flex items-center justify-between mt-3">
              <Typography variant="body2" color={ColorsNeutral400}>
                {t('pyfwdRecipient')}
              </Typography>
              <Typography variant="body2">{contact.enterprise.name}</Typography>
            </Box>
            <Box className="flex items-center justify-between mt-3">
              <Typography variant="body2" color={ColorsNeutral400}>
                {t('pyfwdMovementType')}
              </Typography>
              <Typography variant="body2">{t('pyfwdPaymentMethod')}</Typography>
            </Box>
            <Box className="flex items-center justify-between mt-3">
              <Typography variant="body2" color={ColorsNeutral400}>
                {t('pyfwdExecutionDate')}
              </Typography>
              <Typography variant="body2">
                {executionDate
                  ? DateTime.fromISO(executionDate).toFormat('dd LLLL yyyy', {
                      locale: language,
                    })
                  : ''}
              </Typography>
            </Box>
            <Box className="flex items-center justify-between mt-3">
              <Typography variant="body2" color={ColorsNeutral400}>
                {t('pyfwdDueDate')}
              </Typography>
              <Typography variant="body2">
                {executionDate
                  ? DateTime.fromISO(dueDate).toFormat('dd LLLL yyyy', {
                      locale: language,
                    })
                  : ''}
              </Typography>
            </Box>
            <Box className="flex items-center justify-between mt-3">
              <Typography variant="body2" color={ColorsNeutral400}>
                {t('pyfwdexchangeRate')}
              </Typography>
              <Typography variant="body2">
                {Number(rate?.value).toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </CustomDrawer>
      {showConfirmDialog && (
        <Dialog
          open={showConfirmDialog}
          onClose={() => setShowConfirmDialog(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle
            showCloseButton
            onClick={() => setShowConfirmDialog(false)}
          />
          <DialogContent>
            <Typography variant="h4" color={ColorsPrimary900}>
              {t('pyfwdConfirmTitle')}
            </Typography>
            <Typography variant="h4" className="!my-5" color={ColorsPrimary500}>
              {formatCurrency(remainingAmount)} {currency}
            </Typography>
            <Box className="flex justify-end mt-5">
              <Button
                appearance="secondary"
                variant="outlined"
                color="inherit"
                className="!capitalize !rounded-lg"
                onClick={handleReject}
                disabled={forwardPayedLoading}
                sx={{ marginRight: '1rem' }}
              >
                {t('pyfwdCancelButton')}
              </Button>
              <Button
                appearance="primary"
                variant="contained"
                color="primary"
                className="!capitalize !rounded-lg"
                disabled={forwardPayedLoading}
                onClick={handleConfirm}
                endIcon={
                  forwardPayedLoading && (
                    <RefreshIcon className="!animate-spin" />
                  )
                }
              >
                {t('pyfwdConfirmButton')}
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default PayForward;
